/**
 * The only way the App talks to its API (ADR-0004): same origin (`/api`), the session cookie
 * is HttpOnly and sent by the browser — this code never sees or handles a token. Responses are
 * validated with zod before use, like everything else the App renders.
 */
import type { z } from 'zod';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    readonly detail: Record<string, unknown> = {},
  ) {
    super(code);
    this.name = 'ApiError';
  }
}

type Method = 'GET' | 'POST' | 'PUT' | 'DELETE';

interface Options {
  method?: Method;
  body?: unknown;
  /** Lets a save finish while the page is being closed (code autosave). */
  keepalive?: boolean;
}

export async function api<T>(
  path: string,
  schema: z.ZodType<T>,
  options: Options = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  const init: RequestInit = {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers,
    keepalive: options.keepalive === true,
  };
  if (method !== 'GET') {
    // The API refuses writes that are not JSON (CSRF guard, ADR-0004 §2); the browser adds
    // the Origin header.
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(options.body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(path, init);
  } catch {
    throw new ApiError(0, 'network_error');
  }
  const data: unknown = res.status === 204 ? null : await res.json().catch(() => null);
  if (!res.ok) {
    const record = (typeof data === 'object' && data !== null ? data : {}) as Record<
      string,
      unknown
    >;
    const code = typeof record.error === 'string' ? record.error : 'error';
    throw new ApiError(res.status, code, record);
  }
  return schema.parse(data);
}

export const isApiError = (e: unknown, code?: string): e is ApiError =>
  e instanceof ApiError && (code === undefined || e.code === code);
